/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IActionContext } from '@msdyn365-commerce/core';
import { updateCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { Cart, CartLine, ChannelDeliveryOptionConfiguration } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import _ from 'lodash';

import { ICartActionResultWithCart } from './cart-action-result';

export default async function clearCartLinesDeliveryInformation(cart: Readonly<Cart | undefined>, actionContext: IActionContext, channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration): Promise<ICartActionResultWithCart> {

    if (!cart || !cart.CartLines) {
        return { cart: undefined, status: 'FAILED' };
    }
    let matchingLines: CartLine[] = [];
    const pickupDeliveryModeCode = actionContext && actionContext.requestContext && actionContext.requestContext.channel?.PickupDeliveryModeCode;
    const emailDeliveryModeCode = actionContext && actionContext.requestContext && actionContext.requestContext.channel?.EmailDeliveryModeCode;
    if (channelDeliveryOptionConfig !== undefined) {
        matchingLines = cart.CartLines.filter(
            cartLine => cartLine.DeliveryMode && cartLine.DeliveryMode !== channelDeliveryOptionConfig.PickupDeliveryModeCodes?.find(dM => dM === cartLine.DeliveryMode) && cartLine.DeliveryMode !== emailDeliveryModeCode && cartLine.LineId
        );
    } else {
        matchingLines = cart.CartLines.filter(
            cartLine => cartLine.DeliveryMode && cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode && cartLine.LineId
        );
    }

    // If there are no lines needing updates, just return success
    if (matchingLines.length === 0) {
        return <ICartActionResultWithCart> { cart, status: 'SUCCESS' };
    }

    const copiedLines: CartLine[] = _.cloneDeep(matchingLines);

    copiedLines.forEach(line => {
        line.DeliveryMode = '';
        line.FulfillmentStoreId = '';
        line.ShippingAddress = {};
    });

    return updateCartLinesAsync({ callerContext: actionContext }, cart.Id, copiedLines, null)
        .then((newCart: any) => {
            return <ICartActionResultWithCart> { cart: newCart, status: 'SUCCESS' };
        })
        .catch((error: string) => {
            actionContext.telemetry.warning(error);
            actionContext.telemetry.debug('Unable to Update Cart Lines');

            return <ICartActionResultWithCart> { cart: undefined, status: 'FAILED' };
        });
}
